'use client';

import type { ReactNode } from 'react';
import type { Departement, Region } from '@/libraries/collectivites';
import { inject } from '@/libraries/injection';
import { load$, map$, setZoom } from '@/libraries/map';
import { hrefWithSearchParams } from '@/libraries/nextjs';
import { useSearchParams } from '@/libraries/nextjs/shim';
import { Subscribe, useTap } from '@/libraries/reactivity/Subscribe';
import type { BreadcrumbItem } from '@/libraries/ui/blocks/breadcrumbs';
import { Breadcrumbs } from '@/libraries/ui/blocks/breadcrumbs';
import { contentId } from '@/libraries/ui/blocks/skip-links/skip-links';
import SkipLinksPortal from '@/libraries/ui/blocks/skip-links/skip-links-portal';
import { LocationFranceIllustration } from '@/libraries/ui/pictograms/map/location-france.illustration';
import { ButtonLink } from '@/libraries/ui/primitives/button-link';
import { Link } from '@/libraries/ui/primitives/link';
import { MAP_CONFIG } from '@/shared/injection';
import { highlightRegion } from '@/shared/ui/highlight-decoupage-administratif';

export const DepartementsPage = ({
  region,
  departements,
  totalLieux,
  breadcrumbsItems
}: {
  region: Region;
  departements: Departement[];
  totalLieux: number;
  breadcrumbsItems?: BreadcrumbItem[];
}): ReactNode => {
  const urlSearchParams = useSearchParams();
  const searchParams: URLSearchParams = new URLSearchParams(urlSearchParams);
  const customLocation = inject(MAP_CONFIG);

  const customZoom = customLocation.zoom;
  const isCustomZoomValid = customZoom != null && customZoom > region.zoom;

  const initialLocation = {
    zoom: isCustomZoomValid ? customZoom : region.zoom,
    latitude: customLocation.latitude ?? region.localisation.latitude,
    longitude: customLocation.longitude ?? region.localisation.longitude
  };

  useTap(map$, (map) => {
    setZoom(initialLocation.zoom);
    if (!map) return;
    map.flyTo({
      center: [initialLocation.longitude, initialLocation.latitude],
      zoom: initialLocation.zoom,
      duration: 400
    });
    if (!map.isStyleLoaded()) return;
    highlightRegion(map, region.code);
  });

  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: 'France', href: hrefWithSearchParams('/')(searchParams) },
    { label: region.nom }
  ];

  return (
    <>
      <SkipLinksPortal />
      <main id={contentId} className='flex flex-col justify-between h-full gap-16'>
        <div>
          <Breadcrumbs items={breadcrumbsItems ?? defaultBreadcrumbs} />
          <LocationFranceIllustration className='mt-10 mb-6' />
          <Subscribe to$={load$}>
            {(isLoading) => (
              <h1 className='mb-12 text-3xl text-base-title font-light'>
                {region.nom}
                <br />
                <span className='font-bold'>
                  {isLoading ? 'Chargement des lieux...' : <>{totalLieux} lieux d’inclusion numérique</>}
                </span>
              </h1>
            )}
          </Subscribe>

          <h2 className='font-bold uppercase text-xs text-base-title mb-3'>Filtrer par département</h2>
          <div className='flex flex-wrap gap-1.5'>
            {departements.map(({ code, slug, nom }) => (
              <Link
                href={hrefWithSearchParams(`/${region.slug}/${slug}`)(searchParams, ['page'])}
                key={code}
                prefetch={false}
                className='tag badge-primary badge-soft'
              >
                ({code}) {nom}
              </Link>
            ))}
          </div>
        </div>
        <div className='pb-8'>
          <ButtonLink
            href={hrefWithSearchParams(`/${region.slug}/lieux`)(searchParams)}
            className='border-base-200'
            kind='btn-outline'
            color='btn-primary'
          >
            Afficher la liste des {totalLieux} lieux
          </ButtonLink>
        </div>
      </main>
    </>
  );
};
